/**
 * Vocabulario de la activación: qué le pasó a cada unidad y cómo se pinta.
 *
 * El color es estado y nada más (ver `ui/Badge`): verde = se activó, ámbar =
 * no pasó nada malo pero tampoco se activó, rojo = alguien tiene que sacar
 * esa etiqueta de la caja antes de que salga de planta.
 *
 * Los desenlaces los define `services/api/schemas/activation.py`
 * (`ActivationOutcome`), más `duplicate`, que el endpoint cuenta aparte
 * cuando el mismo código viene dos veces en el mismo lote.
 */
import type { MessageKey } from '@/i18n/messages';
import type { Tone } from '@/ui/Badge';

/** Orden de lectura: primero lo que salió bien, después lo que hay que mirar. */
export const OUTCOME_ORDER = [
  'activated',
  'already_activated',
  'duplicate',
  'wrong_state',
  'other_batch',
  'terminal',
  'unknown',
  'invalid_code',
] as const;

export type ActivationOutcome = (typeof OUTCOME_ORDER)[number];

const TONES: Record<ActivationOutcome, Tone> = {
  activated: 'verified',
  already_activated: 'neutral',
  duplicate: 'neutral',
  // Los cuatro que quedan auditados en `activation_conflict`. Todos rojos: no
  // hay ninguno que sea "normal" en una línea que funciona bien.
  wrong_state: 'critical',
  other_batch: 'critical',
  terminal: 'critical',
  unknown: 'critical',
  invalid_code: 'critical',
};

const LABELS: Record<ActivationOutcome, MessageKey> = {
  activated: 'activation.outcome.activated',
  already_activated: 'activation.outcome.alreadyActivated',
  duplicate: 'activation.outcome.duplicate',
  wrong_state: 'activation.outcome.wrongState',
  other_batch: 'activation.outcome.otherBatch',
  terminal: 'activation.outcome.terminal',
  unknown: 'activation.outcome.unknown',
  invalid_code: 'activation.outcome.invalidCode',
};

const EXPLANATIONS: Record<ActivationOutcome, MessageKey> = {
  activated: 'activation.why.activated',
  already_activated: 'activation.why.alreadyActivated',
  duplicate: 'activation.why.duplicate',
  wrong_state: 'activation.why.wrongState',
  other_batch: 'activation.why.otherBatch',
  terminal: 'activation.why.terminal',
  unknown: 'activation.why.unknown',
  invalid_code: 'activation.why.invalidCode',
};

function known(outcome: string): outcome is ActivationOutcome {
  return (OUTCOME_ORDER as readonly string[]).includes(outcome);
}

/**
 * Tono de un desenlace. Uno desconocido —el servidor agregó uno y este
 * dashboard es viejo— sale neutro: inventarle un color sería afirmar algo
 * que no sabemos.
 */
export function outcomeTone(outcome: string): Tone {
  return known(outcome) ? TONES[outcome] : 'neutral';
}

/** `null` si el desenlace no está en el catálogo: la vista muestra el crudo. */
export function outcomeLabelKey(outcome: string): MessageKey | null {
  return known(outcome) ? LABELS[outcome] : null;
}

export function outcomeExplanationKey(outcome: string): MessageKey | null {
  return known(outcome) ? EXPLANATIONS[outcome] : null;
}

/** ¿Este desenlace deja fila en `activation_conflict`? */
export function isRejection(outcome: string): boolean {
  return (
    outcome === 'other_batch' ||
    outcome === 'terminal' ||
    outcome === 'wrong_state' ||
    outcome === 'unknown'
  );
}

/**
 * Ordena el `outcomes` de la respuesta para mostrarlo siempre igual. Los
 * desenlaces que el catálogo no conoce van al final, no se descartan.
 */
export function sortOutcomes(outcomes: Record<string, number>): [string, number][] {
  return Object.entries(outcomes)
    .filter(([, total]) => total > 0)
    .sort(([a], [b]) => {
      const ia = (OUTCOME_ORDER as readonly string[]).indexOf(a);
      const ib = (OUTCOME_ORDER as readonly string[]).indexOf(b);
      if (ia === ib) return a.localeCompare(b);
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });
}

// ───────────────────────────────────────────────── estados de la unidad

/**
 * `unit.status` tal como se persiste. Espejo de `services/shared/states.py`:
 * los números son parte del contrato con el esquema y no se renumeran.
 */
const UNIT_STATUS: Record<number, MessageKey> = {
  0: 'activation.unitStatus.created',
  1: 'activation.unitStatus.printed',
  2: 'activation.unitStatus.shipped',
  3: 'activation.unitStatus.received',
  4: 'activation.unitStatus.activated',
  5: 'activation.unitStatus.inMarket',
  6: 'activation.unitStatus.verified',
  90: 'activation.unitStatus.voided',
  91: 'activation.unitStatus.stolen',
  92: 'activation.unitStatus.recalled',
  93: 'activation.unitStatus.destroyed',
};

/** `null` si el número no está en el catálogo: se muestra el número pelado. */
export function unitStatusKey(status: number | null | undefined): MessageKey | null {
  if (status === null || status === undefined) return null;
  return UNIT_STATUS[status] ?? null;
}

/** Los tres estados que autentican (`GENUINE_STATUSES`). */
export function isGenuineStatus(status: number | null | undefined): boolean {
  return status === 4 || status === 5 || status === 6;
}
